import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  HelpCircle, Search, Sparkles, Upload, Repeat, TrendingUp, Wallet, Receipt,
  ShieldAlert, Calendar, Banknote, Target, BookOpen, Settings,
  MessageCircle, Lightbulb, Twitter, Facebook, Linkedin,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { Link } from 'react-router-dom';
import { RenderMarkdownInline } from '../lib/markdown';
import { SOCIAL } from '../lib/social';
import PageTitle from '../components/PageTitle';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_LINKS = [
  SOCIAL.twitter.enabled !== false && { label: SOCIAL.twitter.label, href: SOCIAL.twitter.url, Icon: Twitter },
  SOCIAL.facebook.enabled !== false && { label: SOCIAL.facebook.label, href: SOCIAL.facebook.url, Icon: Facebook },
  SOCIAL.linkedin.enabled !== false && { label: SOCIAL.linkedin.label, href: SOCIAL.linkedin.url, Icon: Linkedin },
].filter(Boolean);

// Curated icon set — values stored in the editable page-content as slugs.
const TIP_ICON_MAP = {
  sparkles: Sparkles,
  upload: Upload,
  repeat: Repeat,
  'trending-up': TrendingUp,
  wallet: Wallet,
  receipt: Receipt,
  'shield-alert': ShieldAlert,
  calendar: Calendar,
  banknote: Banknote,
  target: Target,
  'book-open': BookOpen,
  settings: Settings,
  lightbulb: Lightbulb,
};
export const TIP_ICON_SLUGS = Object.keys(TIP_ICON_MAP);
const iconFor = (slug) => TIP_ICON_MAP[slug] || Sparkles;

const TipCard = ({ icon, title, body, idx }) => {
  const Icon = iconFor(icon);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.35, delay: idx * 0.05 }}
      className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:border-[#CBD5E1] transition-colors"
      data-testid={`tip-card-${idx}`}
    >
      <div className="w-9 h-9 rounded-md bg-[#DBEAFE] text-[#1E40AF] flex items-center justify-center mb-3">
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-sm font-semibold text-[#020617] mb-1">{title}</div>
      <div className="text-sm text-[#475569] leading-relaxed">
        <RenderMarkdownInline text={body} />
      </div>
    </motion.div>
  );
};

const Help = () => {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/page-content/help`)
      .then((r) => { if (!cancelled) setContent(r.data); })
      .catch(() => { if (!cancelled) setContent(null); });
    return () => { cancelled = true; };
  }, []);

  const tips = content?.tips || [];

  const filteredFaqs = useMemo(() => {
    const faqs = content?.faqs || [];
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [query, content]);

  const faqs = content?.faqs || [];

  const isStaff = isStaffOrAbove(user);

  return (
    <div className="min-h-screen flex flex-col" data-testid="help-page">
      <Seo
        title="Forge Ledger Help — FAQs and setup tips"
        description="Quick tips, setup guides, and answers to the most common Forge Ledger questions — password reset, tiers, reconciliation, payments and more."
        path="/help"
      />
      <div className="p-6 sm:p-8 max-w-4xl mx-auto flex-1 w-full">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10"
      >
        <PageTitle
          title="Help & FAQ"
          subtitle={content?.hero_subtitle || 'Quick tips to get the most out of Forge Ledger, plus answers to the questions we hear most.'}
          testId="help-title"
        />
        {isStaff && (
          <Link
            to="/admin/edit/help"
            className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-[#0052FF] hover:underline"
            data-testid="help-edit-cta"
          >
            <Settings className="w-3.5 h-3.5" />
            Edit this page (staff only)
          </Link>
        )}
      </motion.div>

      {/* Setup tips */}
      <section className="mb-12" data-testid="help-section-tips">
        <div className="flex items-center gap-2 mb-5">
          <Lightbulb className="w-5 h-5 text-[#0052FF]" />
          <h2 className="text-2xl tracking-tight font-bold text-[#020617]">{content?.tips_heading || 'Setup tips'}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tips.map((t, i) => (
            <TipCard key={`${t.title}-${i}`} icon={t.icon} title={t.title} body={t.body} idx={i} />
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-12" data-testid="help-section-faq">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#0052FF]" />
            <h2 className="text-2xl tracking-tight font-bold text-[#020617]">{content?.faq_heading || 'Frequently asked questions'}</h2>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
            <Input
              type="search"
              placeholder="Search FAQs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              data-testid="help-faq-search"
            />
          </div>
        </div>

        {filteredFaqs.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center" data-testid="help-faq-empty">
            <p className="text-sm text-[#64748B]">No FAQs match &quot;{query}&quot;. Try a different search term or ask the community below.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-lg px-2 sm:px-6">
            <Accordion type="single" collapsible className="w-full">
              {filteredFaqs.map((f, i) => (
                <AccordionItem key={`${f.q}-${i}`} value={`faq-${i}`} data-testid={`help-faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-[15px] font-medium text-[#020617] hover:text-[#0052FF]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[14px] text-[#475569] leading-relaxed">
                    <RenderMarkdownInline text={f.a} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </section>

      {/* Community Q&A */}
      <section data-testid="help-section-community">
        <div className="flex items-center gap-2 mb-5">
          <MessageCircle className="w-5 h-5 text-[#0052FF]" />
          <h2 className="text-2xl tracking-tight font-bold text-[#020617]">{content?.community_heading || 'Community Q&A'}</h2>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8">
          <p className="text-[#334155] mb-4">
            <RenderMarkdownInline text={content?.community_intro || ''} />
          </p>
          <div className="flex flex-wrap gap-3">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#334155] hover:border-[#0052FF] hover:text-[#0052FF] transition-colors"
                data-testid={`help-community-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <s.Icon className="w-4 h-4" />
                {s.label}
              </a>
            ))}
          </div>
          {content?.community_footnote && (
            <p className="text-xs text-[#94A3B8] mt-4">
              <RenderMarkdownInline text={content.community_footnote} />
            </p>
          )}
        </div>
      </section>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default Help;
